import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMethods } from '@lhb/hook';
import { isArray, isNotEmptyAny } from '@lhb/func';
import { checkDynamicFormUploadStatus } from '../../ts-config';
import { footprintCreate, saveChancePoint, submitTaskChancePoint } from '@/common/api/expandStore/chancepoint';
import { checkMatchBusinessProperty, ControlType, getSubmitValuesByTiledFormData, } from '@/common/components/business/DynamicComponent/config';
import { AlwaysRequiredChanceIndentifications, addressIdentification } from '@/common/components/business/DynamicComponent/customize';
import { radioEnum } from '@/common/components/business/DynamicComponent/components/Footprint';
import { checkIsInCluster } from '../CheckIsInCluster';
import styles from '../index.module.less';
import { Button, Space, message } from 'antd';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { useBigdataFn } from '@/common/utils/bigdata';
import { getTaskAssociable } from '@/common/components/business/DynamicComponent/components/AreaProtection/collision';
import { chanceNameIdentification, createMockChancePointId, getMissingLocxxField, isLocxxOpportunityMockEnabled } from '../locxxOpportunity';

/**
 * 机会创建抽屉-底部
 * @description
 * @demo
 */
const Component:FC<{
  id,
  taskId?,
  drawerData,
  assignedData,
  /** 仅从加盟商详情新建时传入，保存接口直接关联来源加盟商。 */
  franchiseeId?: number | string,
  form,
  formData,
  isExpandStoreTaskIn,
  isLocxxTask,
  handleLocxxSave,
  needAssiocateChance,
  supportDirectApproval,
  isFootprintValue,
  saveSucceedRef,
  tiledFormDataRef,
  setClusterHintModalOpen,
  /** 拓店任务-提交点位评估 */
  handleExpandStoreApproved,
  handleCreateSuccessed,
  handleEditSuccessed,
  onSearch?,
  closeDrawer,
  confirmClose,
}> = ({
  id,
  taskId,
  drawerData,
  assignedData,
  franchiseeId,
  form,
  formData,
  isExpandStoreTaskIn,
  isLocxxTask = false,
  handleLocxxSave,
  needAssiocateChance,
  supportDirectApproval,
  isFootprintValue,
  saveSucceedRef,
  tiledFormDataRef,
  setClusterHintModalOpen,
  handleExpandStoreApproved,
  handleCreateSuccessed,
  handleEditSuccessed,
  onSearch,
  closeDrawer,
  confirmClose,
}) => {

  /* state */
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook

  const [requesting, setRequesting] = useState(false);
  const locxxRequestRef = useRef(false);
  const savedLocxxIdRef = useRef(id);
  useEffect(() => {
    savedLocxxIdRef.current = id;
  }, [id, drawerData?.open]);
  const dynamicOtherFormNameArr = useSelector((state: any) => state.common.dynamicOtherFormNameArr); // 存放“其他”表单的formName

  /** 是否益禾堂租户 */
  const isYiHeTang = useMemo(() => {
    return !!tenantCheckData?.isYiHeTang;
  }, [tenantCheckData]);

  /* hooks */

  /* methods */
  const methods = useMethods({

    /** Locxx 的保存与提报使用独立接口；提报部分成功时保留 ID，补充资料后原地重试。 */
    async submitLocxxOpportunity(submit: boolean) {
      if (locxxRequestRef.current || !formData?.dynamicRelationId) return;
      if (!taskId) {
        V2Message.warning('缺少拓店任务信息，请返回任务重新打开表单');
        return;
      }
      locxxRequestRef.current = true;
      setRequesting(true);
      try {
        // 保存校验机会点名称 + 详细地址（与 H5 一致，后端 DE0319999101 要求两者齐全）；提报校验发起节点配置为不为空的字段。
        const missing = getMissingLocxxField(
          tiledFormDataRef.current,
          form.getFieldsValue(true),
          submit ? AlwaysRequiredChanceIndentifications : [chanceNameIdentification, addressIdentification],
          // Locxx 入口按 access 过滤权限；access=null 表示模板未配置发起节点表单权限，按字段自身 required 判断
          isLocxxTask,
          !submit,
        );
        if (missing) {
          V2Message.warning(`请填写${missing.anotherName || missing.name || '必填项'}`);
          return;
        }
        // 提交额外校验当前 Tab 的控件格式规则；保存只校验名称，不校验模板必填与格式。
        if (submit) await form.validateFields();
        if (submit && !checkDynamicFormUploadStatus(tiledFormDataRef.current.filter(item => item.controlType === ControlType.UPLOAD.value))) return;
        const propertyValues = getSubmitValuesByTiledFormData(tiledFormDataRef.current);
        // 商圈一致性校验：保存和提交都需要，避免 422 后端报错（DE0319999101）。
        if (!checkIsInCluster(formData, propertyValues)) {
          setClusterHintModalOpen(true);
          return;
        }
        const params: any = {
          id: savedLocxxIdRef.current || undefined,
          customerTypeId: formData.customerTypeId,
          dynamicRelationId: formData.dynamicRelationId,
          propertyValues,
          taskAssociable: getTaskAssociable(propertyValues, undefined, !!tenantCheckData?.isSjtb),
        };
        const mocked = isLocxxOpportunityMockEnabled();
        if (submit) {
          params.standardTaskId = taskId;
          const result = mocked
            ? { chancePointId: savedLocxxIdRef.current || createMockChancePointId(), saved: true, evaluationStarted: true }
            : await submitTaskChancePoint(params).then(res => res, err => { throw err; }).finally(() => {});
          if (result?.chancePointId) savedLocxxIdRef.current = result.chancePointId;
          if (result?.saved !== true || !result.chancePointId || result.evaluationStarted !== true) {
            V2Message.warning(result?.detailMessage || result?.message || '点位评估未发起，请补充资料后重试');
            return;
          }
        } else {
          const savedId = mocked ? savedLocxxIdRef.current || createMockChancePointId() : await saveChancePoint(params).then(res => res, err => { throw err; }).finally(() => {});
          if (!savedId || typeof savedId !== 'number') throw new Error('保存结果缺少机会点 ID，请刷新核实');
          savedLocxxIdRef.current = savedId;
          // PC 保存接口不负责关联任务；失败时仍保留已保存 ID，重试不会再创建。
          if (!mocked) await handleLocxxSave?.(savedId);
        }
        V2Message.success(submit ? '提交点位评估成功' : '保存成功');
        if (id) handleEditSuccessed?.();
        else handleCreateSuccessed?.(savedLocxxIdRef.current);
        onSearch?.({});
        confirmClose();
      } catch (error: any) {
        const msg = error?.errorFields?.[0]?.errors?.[0] || // 表单校验错误
          error?.response?.data?.errMsg || // 后端 422 等业务错误
          error?.response?.data?.errorMsg || // 后端其他格式
          error?.message || // 网络/运行时错误
          '操作失败，请重试';
        V2Message.warning(msg);
      } finally {
        locxxRequestRef.current = false;
        setRequesting(false);
      }
    },

    /**
     * 表单提交，目前的写法只能校验当前表单页的必填项，因为渲染的时候只渲染对应的tab下的表单项
     * @param isApproveForStore 拓店任务-点位评估场景下，点击提交时传true，后续需要进行点位评估
     */
    confirmHandle(isApproveForStore = false) {

      if (isLocxxTask) return methods.submitLocxxOpportunity(isApproveForStore);

      // 调试获取表单数据
      console.log('form.getFieldsValue()', form.getFieldsValue());

      // 已保存成功，不再处理
      if (saveSucceedRef.current) {
        return;
      }
      // 无论任何情况下，机会点表单必须检查必填的特殊字段
      let checkFields: any[] = [];
      const needCheck = needAssiocateChance || isApproveForStore;
      if (!needCheck && isArray(tiledFormDataRef.current)) {
        const allIndentifications = tiledFormDataRef.current.map(itm => itm.identification);
        checkFields = AlwaysRequiredChanceIndentifications.filter((itm: any) => allIndentifications.includes(itm));
        // 普通保存也校验模板中必填的树形选择，其他类型保持原有校验范围。
        checkFields = checkFields.concat(tiledFormDataRef.current
          .filter(itm => itm.controlType === ControlType.TREE_SELECT.value && itm.required && itm.isShow !== false)
          .map(itm => itm.identification));
        // 上传类型的组件也要校验是否上传完毕，如果还在上传中也要校验
        const uploadComs = tiledFormDataRef.current?.filter((itm: any) => itm.controlType === ControlType.UPLOAD.value);
        if (!checkDynamicFormUploadStatus(uploadComs)) return;
      }

      // 创建机会点后自动关联任务场景下，需要检查匹配商圈组件值（目前只有益禾堂租户会检查其中的集客点是否已选择）
      if (needCheck && !checkMatchBusinessProperty(isYiHeTang, tiledFormDataRef.current)) {
        V2Message.warning('请先选择商圈内集客点'); // 产品定的文案
        return;
      }

      // 全部字段检查时传undefined即无入参
      form
        .validateFields(needCheck ? undefined : [...checkFields, ...dynamicOtherFormNameArr])
        .then(async () => {
          // 拿到踩点组件的值，校验信息是否填写完全（对目前的写法只能校验当前表单页的必填项的校验补充）
          const footprintItem:any = tiledFormDataRef.current.filter((item) => {
            return item.identification === 'locationFootprint';
          });
          let footprintValue;
          if (footprintItem.length !== 0 && isArray(footprintItem)) {
            footprintValue = JSON.parse(footprintItem[0]?.textValue || null);
          }
          // 与产品大鹏确认，对于审批流程配置和可见可写不为空配置中，在supportDirectApproval===1情况下（支持审批流程配置)仅当access===2（可写）的状态下需要校验踩点组件是否不为空；supportDirectApproval===2情况下，表单都可见，所以也需要判断是否填写踩点组件
          let access;
          if (footprintItem.length !== 0 && isArray(footprintItem)) {
            access = JSON.parse(footprintItem[0]?.access || null);
          }
          // footprintItem不为空数组，选了是没填消息，或者没选
          if ((isArray(footprintItem) && footprintItem.length) &&
          ((supportDirectApproval === 1 && access === 2) || supportDirectApproval === 2) &&
          (footprintValue?.dynamicComponent_footprint_isStart === radioEnum.AGREE || !isNotEmptyAny(footprintValue))) {
            // 判断踩点组件信息是否填写完全
            if (!isNotEmptyAny(footprintValue?.dynamicComponent_footprint_checkRule) ||
              !isNotEmptyAny(footprintValue?.dynamicComponent_footprint_checkPeriod)) {
              message.error('踩点信息填写不完全');
              return;
            }
          }

          if (requesting) return;
          setRequesting(true);

          const propertyValues = getSubmitValuesByTiledFormData(tiledFormDataRef.current);
          // if (process.env.NODE_ENV === 'development' && propertyValues) {
          //   console.log(`表单submit的值`, propertyValues);
          //   return;
          // }
          const params: any = {
            // type: formData.type,
            customerTypeId: formData.customerTypeId,
            dynamicRelationId: formData.dynamicRelationId,
            propertyValues,
            taskAssociable: getTaskAssociable(propertyValues, undefined, !!tenantCheckData?.isSjtb),
          };
          if (id) {
            params.id = id;
          } else if (isNotEmptyAny(franchiseeId)) {
            params.franchiseeId = franchiseeId;
          }
          // 按照表单数据判断是否机会点是否在所属商圈中
          if (!checkIsInCluster(formData, propertyValues)) {
            setClusterHintModalOpen(true);
            setRequesting(false);
            return;
          }
          const data = await saveChancePoint(params).finally(() => {
            setRequesting(false);
          }); // 确保获得的data是机会点id，格式变动会影响后续功能
          setRequesting(false);

          // 拿到机会点的 id=>data，需要创建踩点任务 且不是在编辑机会点情况下 再创建踩点任务
          if (footprintValue?.dynamicComponent_footprint_isStart === radioEnum.AGREE && !isFootprintValue) {
            if (!data) {
              message.error('机会点尚未保存');
              return;
            }
            await footprintCreate({
              chancePointId: data,
              propertyValues,
              checkRule: footprintValue.dynamicComponent_footprint_checkRule,
              checkPeriod: [{
                start: footprintValue.dynamicComponent_footprint_checkPeriod[0],
                end: footprintValue.dynamicComponent_footprint_checkPeriod[1],
              }]
            });

          }

          if (!isExpandStoreTaskIn && !isLocxxTask) {
            form.resetFields(); // 清空表单
          }
          // 编辑时更新机会点详情
          if (id) {
            handleEditSuccessed?.();
          } else {
            handleCreateSuccessed?.(data);
          }
          if (onSearch) {
            // 客流计数创建成功后回传机会点id
            // 拓店任务需要自动关联机会场景创建成功后回传机会点id
            (isArray(assignedData) && assignedData.length || needAssiocateChance) ? onSearch(data) : onSearch({});
          }
          // onSearch && onSearch({}); // 刷新列表
          bigdataFn('d01dc884-b271-4767-a623-e4d669ab8b18', '创建机会点详情-确定', '点击了创建机会点详情-确定', { op_block: '创建机会点', op_event: ``, });

          if (isApproveForStore && isExpandStoreTaskIn) {
            drawerData.propertyValues = propertyValues;
            handleExpandStoreApproved?.(drawerData.expandStoreData, drawerData);
            return;
          }

          confirmClose(); // 关闭抽屉
        })
        .catch((errorInfo: any) => {
          console.log(`errorInfo1`, errorInfo);
          if (isArray(errorInfo?.errorFields) && errorInfo?.errorFields.length && isArray(errorInfo?.errorFields[0].errors) && errorInfo?.errorFields[0].errors.length) {
            V2Message.warning(errorInfo?.errorFields[0].errors[0]);
          }
        });
    },

  });

  return (<Space size={12} className={styles.footerCon}>
    <Button onClick={closeDrawer}>取消</Button>
    <Button type='primary' loading={requesting} onClick={() => methods.confirmHandle()}>{isExpandStoreTaskIn || isLocxxTask ? '保存' : '确认'}</Button>
    {isLocxxTask
      ? <Button type='primary' loading={requesting} onClick={() => methods.submitLocxxOpportunity(true)}>提交</Button>
      : isExpandStoreTaskIn ? <Button type='primary' loading={requesting} onClick={() => methods.confirmHandle(true)}>提交</Button> : <></>}
  </Space>);
};

export default Component;
